import { useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound, Check } from "lucide-react";
import api, { extractAxiosError } from "../../lib/api";

export default function ChangePasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setDone(false);
    if (next.length < 6) {
      setError(t("auth.resetMinPwd"));
      return;
    }
    if (next !== confirm) {
      setError(t("auth.resetMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(extractAxiosError(err, t("auth.resetErrorGeneric")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <div>
        <label className="label-tech block mb-2">{t("settings.currentPassword")}</label>
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          minLength={1}
          className="w-full px-4 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
          data-testid="change-current-pwd"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label-tech block mb-2">{t("auth.newPassword")}</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            data-testid="change-new-pwd"
          />
        </div>
        <div>
          <label className="label-tech block mb-2">{t("auth.confirmPassword")}</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            data-testid="change-confirm-pwd"
          />
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-xs" data-testid="change-pwd-error">
          {error}
        </div>
      )}

      {done && (
        <div className="px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-xs flex items-center gap-2" data-testid="change-pwd-success">
          <Check size={12} /> {t("settings.passwordChanged")}
        </div>
      )}

      <button
        type="submit"
        disabled={busy || !current || !next || !confirm}
        className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="change-pwd-submit"
      >
        <KeyRound size={14} /> {busy ? t("auth.savingPassword") : t("settings.updatePassword")}
      </button>
    </form>
  );
}
